import { request, requestPublic } from './client';
import type { SportConfig, ExternalGame, TeamMapping } from '../types';

export interface GameWithTeams extends ExternalGame {
  teamMappings: TeamMapping[];
}

export interface SyncResult {
  gamesSynced: number;
  teamsSynced: number;
  teamsError?: string;
}

export interface UpdateSportConfigInput {
  enabled?: boolean;
  seasonYear?: number;
  quarters?: number;
  liveBoardCreditCost?: number;
}

// In-memory cache for game data to deduplicate concurrent requests
// (e.g., multiple BoardCards fetching the same game on the list page)
const gameCache = new Map<string, Promise<GameWithTeams>>();

// Public API - available to all users
export const sportsApi = {
  getEnabledSports: (): Promise<SportConfig[]> =>
    requestPublic('/api/squareup/sports'),

  getUpcomingGames: (sportKey: string): Promise<ExternalGame[]> =>
    request(`/api/squareup/sports/${sportKey}/games`),

  getTeamMappings: (sportKey: string): Promise<TeamMapping[]> =>
    request(`/api/squareup/sports/${sportKey}/teams`),

  getGame: (gameId: string): Promise<GameWithTeams> => {
    if (!gameCache.has(gameId)) {
      const promise = requestPublic<GameWithTeams>(`/api/squareup/sports/games/${gameId}`);
      gameCache.set(gameId, promise);
      // Remove from cache after settling so future calls get fresh data
      promise.finally(() => { setTimeout(() => gameCache.delete(gameId), 30000); });
    }
    return gameCache.get(gameId)!;
  },
};

// Admin API - requires admin role
export const adminSportsApi = {
  getAllSportConfigs: (): Promise<SportConfig[]> =>
    request('/api/squareup/admin/sports'),

  updateSportConfig: (sportKey: string, input: UpdateSportConfigInput): Promise<SportConfig> =>
    request(`/api/squareup/admin/sports/${sportKey}`, {
      method: 'PUT',
      body: input,
    }),

  syncGames: (sportKey: string): Promise<SyncResult> =>
    request(`/api/squareup/admin/sports/${sportKey}/sync`, {
      method: 'POST',
    }),
};
